import React from 'react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function GalaxyLogin({ onLogin }) {
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [showSetup, setShowSetup] = React.useState(false);
  const [setupRequired, setSetupRequired] = React.useState(false);
  const [checkingSetup, setCheckingSetup] = React.useState(true);

  // Setup form fields
  const [setupData, setSetupData] = React.useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    setupKey: ''
  });

  // Check if system needs setup on mount
  React.useEffect(() => {
    checkSetupStatus();
  }, []);

  const checkSetupStatus = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/setup/status`);
      setSetupRequired(response.data.setup_required);
      if (response.data.setup_required) {
        setShowSetup(true);
      }
    } catch (error) {
      console.error('Error checking setup status:', error);
      setSetupRequired(false);
    } finally {
      setCheckingSetup(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      toast.error('Please enter email and password');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/login`, {
        email,
        password
      });

      const { access_token, refresh_token, user, enabled_modules } = response.data;

      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      localStorage.setItem('user', JSON.stringify(user));

      if (enabled_modules) {
        localStorage.setItem('enabled_modules', JSON.stringify(enabled_modules));
      }

      onLogin(access_token, user);
      toast.success(`Welcome back, ${user.name}!`);
    } catch (error) {
      console.error('Login error:', error);
      if (error.response?.status === 401) {
        toast.error('Invalid email or password');
      } else {
        toast.error(error.response?.data?.detail || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSetup = async (e) => {
    e.preventDefault();

    if (!setupData.name || !setupData.email || !setupData.password || !setupData.setupKey) {
      toast.error('Please fill all required fields');
      return;
    }

    if (setupData.password !== setupData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (setupData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/setup/initialize`, {
        name: setupData.name,
        email: setupData.email,
        password: setupData.password,
        setup_key: setupData.setupKey
      });

      const { access_token, refresh_token } = response.data;

      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);

      const user = {
        id: 'super_admin',
        name: setupData.name,
        email: setupData.email,
        role_name: 'Super Admin',
        is_super_admin: true
      };
      localStorage.setItem('user', JSON.stringify(user));

      toast.success('System initialized successfully! Welcome, Super Admin!');
      onLogin(access_token, user);
    } catch (error) {
      console.error('Setup error:', error);
      const errorMessage = error.response?.data?.detail || 'Setup failed. Please try again.';

      if (errorMessage.toLowerCase().includes('already initialized')) {
        toast.info('System is already set up. Please log in with your credentials.');
        setSetupRequired(false);
        setShowSetup(false);
        checkSetupStatus();
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  /* iter-115 — Premium charcoal+indigo Login (Phase 2). All form fields,
     labels, data-testid attributes, and handlers are preserved 1:1. Only
     the visual layer is upgraded: deep slate radial wash, frosted glass
     card, refined typography, indigo accent. */

  if (checkingSetup) {
    return (
      <div className="min-h-screen flex items-center justify-center page-wash-login">
        <div className="text-center anim-fade-in">
          <div className="w-14 h-14 border-2 border-indigo-400/30 border-t-indigo-400 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400 text-sm font-medium tracking-wide">Checking system status…</p>
        </div>
      </div>
    );
  }

  // Shared input styling — keeps every input perfectly consistent
  const inputCx = "w-full px-4 py-3 bg-white/[0.04] border border-white/10 rounded-xl text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400/60 focus:bg-white/[0.06] transition-all duration-240 ease-out";
  const labelCx = "block text-[12.5px] font-medium text-slate-300 mb-1.5 tracking-wide uppercase letter-spacing-wider";

  return (
    <div className="min-h-screen relative overflow-hidden flex items-center justify-center page-wash-login">
      {/* Subtle ambient orbs — refined, not bouncing */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[10%] left-[8%] w-[28rem] h-[28rem] rounded-full opacity-[0.18]" style={{ background: 'radial-gradient(circle, #6366F1 0%, transparent 65%)', filter: 'blur(40px)' }}></div>
        <div className="absolute bottom-[8%] right-[6%] w-[32rem] h-[32rem] rounded-full opacity-[0.12]" style={{ background: 'radial-gradient(circle, #A855F7 0%, transparent 65%)', filter: 'blur(60px)' }}></div>
        <div className="absolute top-[40%] left-[45%] w-[20rem] h-[20rem] rounded-full opacity-[0.08]" style={{ background: 'radial-gradient(circle, #818CF8 0%, transparent 70%)', filter: 'blur(80px)' }}></div>
        {/* Grid texture overlay */}
        <div className="absolute inset-0 opacity-[0.025]" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)',
          backgroundSize: '64px 64px'
        }}></div>
      </div>

      {/* Login Card */}
      <div className="relative z-10 w-full max-w-md px-6 anim-fade-in">
        {/* Soft glow behind card */}
        <div className="absolute -inset-1 rounded-3xl opacity-30 pointer-events-none" style={{ background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)', filter: 'blur(28px)' }}></div>

        <div className="relative bg-slate-900/60 backdrop-blur-2xl border border-white/[0.08] rounded-2xl p-8 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)]">
          {/* Logo */}
          <div className="text-center mb-7 anim-slide-up">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-[0_8px_24px_rgba(99,102,241,0.35)] transition-transform duration-300 hover:scale-105" style={{ background: 'linear-gradient(135deg, #4338CA 0%, #6D28D9 50%, #9333EA 100%)' }}>
              <span className="text-3xl">🚗</span>
            </div>
            <h1 className="text-[26px] font-semibold text-white mb-1.5 tracking-tight" style={{ fontFamily: 'Geist, Inter, sans-serif' }}>AutoBook Pro</h1>
            <p className="text-slate-400 text-[12.5px] font-medium tracking-wide">
              {showSetup && setupRequired ? 'System Setup' : 'Automobile Management System'}
            </p>
          </div>

          {/* Setup Required Banner */}
          {setupRequired && !showSetup && (
            <div className="mb-5 p-3 bg-amber-500/10 border border-amber-400/20 rounded-xl">
              <p className="text-amber-300 text-[12.5px]">
                System not initialized. <button onClick={() => setShowSetup(true)} className="text-amber-200 underline font-medium hover:text-amber-100">Setup now</button>
              </p>
            </div>
          )}

          {showSetup && setupRequired ? (
            /* Setup Form */
            <form onSubmit={handleSetup} className="space-y-4 anim-slide-up">
              <div className="text-center mb-4">
                <p className="text-slate-400 text-[12.5px]">Create your Super Admin account to get started</p>
              </div>

              <div>
                <label className={labelCx}>Full Name *</label>
                <input
                  type="text"
                  value={setupData.name}
                  onChange={(e) => setSetupData(prev => ({...prev, name: e.target.value}))}
                  placeholder="John Doe"
                  className={inputCx}
                  data-testid="setup-name"
                />
              </div>

              <div>
                <label className={labelCx}>Email Address *</label>
                <input
                  type="email"
                  value={setupData.email}
                  onChange={(e) => setSetupData(prev => ({...prev, email: e.target.value}))}
                  placeholder="admin@company.com"
                  className={inputCx}
                  data-testid="setup-email"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCx}>Password *</label>
                  <input
                    type="password"
                    value={setupData.password}
                    onChange={(e) => setSetupData(prev => ({...prev, password: e.target.value}))}
                    placeholder="••••••"
                    className={inputCx}
                    data-testid="setup-password"
                  />
                </div>
                <div>
                  <label className={labelCx}>Confirm *</label>
                  <input
                    type="password"
                    value={setupData.confirmPassword}
                    onChange={(e) => setSetupData(prev => ({...prev, confirmPassword: e.target.value}))}
                    placeholder="••••••"
                    className={inputCx}
                    data-testid="setup-confirm-password"
                  />
                </div>
              </div>

              <div>
                <label className={labelCx}>Setup Key *</label>
                <input
                  type="password"
                  value={setupData.setupKey}
                  onChange={(e) => setSetupData(prev => ({...prev, setupKey: e.target.value}))}
                  placeholder="Enter setup key"
                  className={inputCx}
                  data-testid="setup-key"
                />
                <p className="text-[11px] text-slate-500 mt-1.5">Contact your system administrator for the setup key</p>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 text-white font-medium rounded-xl transition-all duration-240 ease-out disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_8px_24px_rgba(99,102,241,0.4)] hover:shadow-[0_12px_32px_rgba(99,102,241,0.55)] hover:-translate-y-[1px] active:translate-y-0"
                style={{ background: 'linear-gradient(135deg, #4338CA 0%, #6D28D9 60%, #9333EA 100%)' }}
                data-testid="setup-submit"
              >
                {loading ? 'Setting up…' : 'Initialize System'}
              </button>
            </form>
          ) : (
            /* Login Form */
            <form onSubmit={handleLogin} className="space-y-5 anim-slide-up">
              <div>
                <label className={labelCx}>Email Address</label>
                <div className="relative">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className={`${inputCx} pr-11`}
                    data-testid="login-email"
                  />
                  <div className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-500">
                    <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                    </svg>
                  </div>
                </div>
              </div>

              <div>
                <label className={labelCx}>Password</label>
                <div className="relative">
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className={`${inputCx} pr-11`}
                    data-testid="login-password"
                  />
                  <div className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-500">
                    <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between text-[12.5px]">
                <label className="flex items-center text-slate-400 cursor-pointer hover:text-slate-300 transition-colors">
                  <input type="checkbox" className="w-3.5 h-3.5 rounded border-white/20 bg-white/[0.04] text-indigo-500 focus:ring-indigo-400/30 focus:ring-offset-0 mr-2" />
                  Remember me
                </label>
                <a href="#" className="text-indigo-400 hover:text-indigo-300 transition-colors font-medium">Forgot password?</a>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 text-white font-medium rounded-xl transition-all duration-240 ease-out disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-[0_8px_24px_rgba(99,102,241,0.4)] hover:shadow-[0_12px_32px_rgba(99,102,241,0.55)] hover:-translate-y-[1px] active:translate-y-0"
                style={{ background: 'linear-gradient(135deg, #4338CA 0%, #6D28D9 60%, #9333EA 100%)' }}
                data-testid="login-submit"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-[18px] w-[18px]" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Signing in…
                  </span>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>
          )}
        </div>

        <p className="text-center text-slate-500 text-[12px] mt-6 font-medium tracking-wide">
          © 2026 AutoBook Pro. All rights reserved.
        </p>
      </div>
    </div>
  );
}

export default GalaxyLogin;
